import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Calculator, Heart, Brain } from 'lucide-react';

// CSS styles
const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '32px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '8px'
  },
  subtitle: {
    color: '#666',
    marginBottom: '32px'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '32px'
  },
  section: {
    marginBottom: '24px'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    borderBottom: '2px solid #e5e5e5',
    paddingBottom: '8px',
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    marginBottom: '4px'
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '16px',
    marginBottom: '16px',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '16px',
    marginBottom: '16px',
    backgroundColor: 'white',
    boxSizing: 'border-box'
  },
  checkbox: {
    marginRight: '8px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '12px',
    fontSize: '14px',
    color: '#333'
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
    marginBottom: '16px'
  },
  resultCard: {
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '24px'
  },
  cognitiveCard: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #90caf9'
  },
  ascvdCard: {
    backgroundColor: '#ffebee',
    border: '1px solid #ef9a9a'
  },
  resultHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px'
  },
  resultTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  resultValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '8px'
  },
  cognitiveValue: {
    color: '#1976d2'
  },
  ascvdValue: {
    color: '#d32f2f'
  },
  riskBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: '500'
  },
  lowRisk: {
    backgroundColor: '#c8e6c9',
    color: '#2e7d32'
  },
  moderateRisk: {
    backgroundColor: '#fff9c4',
    color: '#f57f17'
  },
  highRisk: {
    backgroundColor: '#ffcdd2',
    color: '#c62828'
  },
  infoBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px'
  },
  infoHeader: {
    display: 'flex',
    gap: '12px'
  },
  infoIcon: {
    color: '#f39c12',
    flexShrink: 0
  },
  conversionBox: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '16px'
  },
  conversionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  conversionText: {
    fontSize: '12px',
    color: '#666',
    lineHeight: '1.5'
  },
  fieldGroup: {
    marginBottom: '24px'
  },
  fieldGroupTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px'
  },
  placeholder: {
    color: '#999',
    fontStyle: 'italic'
  },
  unitHint: {
    fontSize: '12px',
    color: '#666',
    marginLeft: '8px'
  },
  iconBlue: {
    color: '#1976d2'
  },
  iconRed: {
    color: '#d32f2f'
  }
};

const RiskCalculator = () => {
  // State for all inputs
  const [inputs, setInputs] = useState({
    // Common inputs
    age: '',
    mmseBaseline: '',
    mobility: 'independent',
    
    // Cognitive impairment specific
    onAntidepressants: false,
    atrialFibrillation: false,
    groceryShopping: 'independent',
    
    // ASCVD specific
    employed: false,
    onAspirin: false,
    onCholesterolMeds: false,
    onBPMeds: false,
    systolicBP: '',
    healthStatus: 'good',
    hdlCholesterol: '', // in mmol/L
    triglycerides: '', // in mmol/L
    creatinine: '', // in μmol/L
  });

  const [risks, setRisks] = useState({
    cognitive: null,
    ascvd: null
  });

  // Unit conversion functions
  const mmolToMgdl = (mmol) => mmol * 38.67;
  const umolToMgdl = (umol) => umol / 88.4;

  // Calculate cognitive impairment risk
  const calculateCognitiveRisk = useCallback(() => {
    const { age, onAntidepressants, atrialFibrillation, mmseBaseline, mobility, groceryShopping } = inputs;
    
    if (!age || !mmseBaseline) return null;

    let LP = 2.3906 + 
      0.0848 * parseFloat(age) + 
      0.5630 * (onAntidepressants ? 1 : 0) + 
      0.4715 * (atrialFibrillation ? 1 : 0) - 
      0.4090 * parseFloat(mmseBaseline) + 
      0.2519 * (mobility === 'assistance' ? 1 : 0) + 
      1.3220 * (mobility === 'immobile' ? 1 : 0) + 
      0.6221 * (groceryShopping !== 'independent' ? 1 : 0);

    const probability = 1 / (1 + Math.exp(-LP));
    return probability * 100; // Return as percentage
  }, [inputs]);

  // Calculate ASCVD risk
  const calculateASCVDRisk = useCallback(() => {
    const { 
      employed, mmseBaseline, onAspirin, onCholesterolMeds, onBPMeds,
      systolicBP, healthStatus, hdlCholesterol, triglycerides, creatinine, mobility 
    } = inputs;

    if (!mmseBaseline || !systolicBP || !hdlCholesterol || !triglycerides || !creatinine) return null;

    // Convert units
    const hdlMgdl = mmolToMgdl(parseFloat(hdlCholesterol));
    const trigMgdl = mmolToMgdl(parseFloat(triglycerides));
    const creatMgdl = umolToMgdl(parseFloat(creatinine));

    // Calculate spline terms for triglycerides
    const trig = trigMgdl;
    const d1 = (Math.pow(Math.max(trig - 60, 0), 3) - Math.pow(Math.max(trig - 268, 0), 3)) / (268 - 60);
    const d3 = (Math.pow(Math.max(trig - 140, 0), 3) - Math.pow(Math.max(trig - 268, 0), 3)) / (268 - 140);
    const trig2 = d1 - d3;
    
    const d2 = (Math.pow(Math.max(trig - 98, 0), 3) - Math.pow(Math.max(trig - 268, 0), 3)) / (268 - 98);
    const trig3 = d2 - d3;

    let LP = -0.2589 * (employed ? 1 : 0) - 
      0.0836 * parseFloat(mmseBaseline) - 
      0.3155 * (onAspirin ? 1 : 0) - 
      0.3114 * (onCholesterolMeds ? 1 : 0) + 
      0.2801 * (onBPMeds ? 1 : 0) + 
      0.0100 * parseFloat(systolicBP) + 
      0.5610 * (healthStatus === 'fair' ? 1 : 0) + 
      0.4059 * (healthStatus === 'poor' ? 1 : 0) - 
      0.0159 * hdlMgdl - 
      0.0101 * trig + 
      0.00029 * trig2 - 
      0.00046 * trig3 + 
      0.1966 * creatMgdl + 
      0.2914 * (mobility === 'assistance' ? 1 : 0) + 
      0.2363 * (mobility === 'immobile' ? 1 : 0);

    const CIF0 = 0.84822851;
    const probability = 1 - Math.pow(1 - CIF0, Math.exp(LP));
    return probability * 100; // Return as percentage
  }, [inputs]);

  // Update risks when inputs change
  useEffect(() => {
    setRisks({
      cognitive: calculateCognitiveRisk(),
      ascvd: calculateASCVDRisk()
    });
  }, [inputs, calculateCognitiveRisk, calculateASCVDRisk]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const getRiskLevel = (risk) => {
    if (risk === null) return { level: 'unknown', style: {} };
    if (risk < 10) return { level: 'Low', style: styles.lowRisk };
    if (risk < 20) return { level: 'Moderate', style: styles.moderateRisk };
    return { level: 'High', style: styles.highRisk };
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>
          Cognitive Impairment & ASCVD Risk Calculator
        </h1>
        <p style={styles.subtitle}>
          Australian units version - Based on the multi-cohort older adult study
        </p>

        <div style={styles.grid}>
          {/* Input Section */}
          <div>
            <h2 style={styles.sectionTitle}>
              Patient Information
            </h2>

            {/* Common Inputs */}
            <div style={styles.section}>
              <div>
                <label style={styles.label}>
                  Age (years)
                </label>
                <input
                  type="number"
                  min="65"
                  max="100"
                  value={inputs.age}
                  onChange={(e) => handleInputChange('age', e.target.value)}
                  style={styles.input}
                  placeholder="65-100"
                />
              </div>

              <div>
                <label style={styles.label}>
                  Baseline MMSE Score
                </label>
                <input
                  type="number"
                  min="24"
                  max="30"
                  value={inputs.mmseBaseline}
                  onChange={(e) => handleInputChange('mmseBaseline', e.target.value)}
                  style={styles.input}
                  placeholder="24-30"
                />
              </div>

              <div>
                <label style={styles.label}>
                  Mobility Status
                </label>
                <select
                  value={inputs.mobility}
                  onChange={(e) => handleInputChange('mobility', e.target.value)}
                  style={styles.select}
                >
                  <option value="independent">Independently mobile</option>
                  <option value="assistance">Assistance needed</option>
                  <option value="immobile">Immobile or largely immobile</option>
                </select>
              </div>
            </div>

            {/* Cognitive Risk Factors */}
            <div style={styles.fieldGroup}>
              <h3 style={styles.fieldGroupTitle}>
                <Brain size={20} style={styles.iconBlue} />
                Cognitive Risk Factors
              </h3>

              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={inputs.onAntidepressants}
                  onChange={(e) => handleInputChange('onAntidepressants', e.target.checked)}
                  style={styles.checkbox}
                />
                On Antidepressants
              </label>

              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={inputs.atrialFibrillation}
                  onChange={(e) => handleInputChange('atrialFibrillation', e.target.checked)}
                  style={styles.checkbox}
                />
                History of Atrial Fibrillation
              </label>

              <div>
                <label style={styles.label}>
                  Grocery Shopping Ability
                </label>
                <select
                  value={inputs.groceryShopping}
                  onChange={(e) => handleInputChange('groceryShopping', e.target.value)}
                  style={styles.select}
                >
                  <option value="independent">Independent</option>
                  <option value="help">Need some help or dependent</option>
                </select>
              </div>
            </div>

            {/* ASCVD Risk Factors */}
            <div style={styles.fieldGroup}>
              <h3 style={styles.fieldGroupTitle}>
                <Heart size={20} style={styles.iconRed} />
                Cardiovascular Risk Factors
              </h3>

              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={inputs.employed}
                  onChange={(e) => handleInputChange('employed', e.target.checked)}
                  style={styles.checkbox}
                />
                Currently Employed
              </label>

              <div style={styles.checkboxGrid}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={inputs.onAspirin}
                    onChange={(e) => handleInputChange('onAspirin', e.target.checked)}
                    style={styles.checkbox}
                  />
                  Aspirin
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={inputs.onCholesterolMeds}
                    onChange={(e) => handleInputChange('onCholesterolMeds', e.target.checked)}
                    style={styles.checkbox}
                  />
                  Statins
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={inputs.onBPMeds}
                    onChange={(e) => handleInputChange('onBPMeds', e.target.checked)}
                    style={styles.checkbox}
                  />
                  BP Meds
                </label>
              </div>

              <div>
                <label style={styles.label}>
                  Systolic Blood Pressure (mmHg)
                </label>
                <input
                  type="number"
                  min="90"
                  max="200"
                  value={inputs.systolicBP}
                  onChange={(e) => handleInputChange('systolicBP', e.target.value)}
                  style={styles.input}
                  placeholder="90-200"
                />
              </div>

              <div>
                <label style={styles.label}>
                  General Health Status
                </label>
                <select
                  value={inputs.healthStatus}
                  onChange={(e) => handleInputChange('healthStatus', e.target.value)}
                  style={styles.select}
                >
                  <option value="good">Excellent/Very Good/Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                </select>
              </div>

              <div>
                <label style={styles.label}>
                  HDL Cholesterol (mmol/L)
                  <span style={styles.unitHint}>Australian units</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="3.0"
                  value={inputs.hdlCholesterol}
                  onChange={(e) => handleInputChange('hdlCholesterol', e.target.value)}
                  style={styles.input}
                  placeholder="0.5-3.0"
                />
              </div>

              <div>
                <label style={styles.label}>
                  Triglycerides (mmol/L)
                  <span style={styles.unitHint}>Australian units</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="10.0"
                  value={inputs.triglycerides}
                  onChange={(e) => handleInputChange('triglycerides', e.target.value)}
                  style={styles.input}
                  placeholder="0.5-10.0"
                />
              </div>

              <div>
                <label style={styles.label}>
                  Creatinine (μmol/L)
                  <span style={styles.unitHint}>Australian units</span>
                </label>
                <input
                  type="number"
                  min="40"
                  max="200"
                  value={inputs.creatinine}
                  onChange={(e) => handleInputChange('creatinine', e.target.value)}
                  style={styles.input}
                  placeholder="40-200"
                />
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div>
            <h2 style={styles.sectionTitle}>
              Risk Assessment Results
            </h2>

            {/* Cognitive Risk Result */}
            <div style={{...styles.resultCard, ...styles.cognitiveCard}}>
              <div style={styles.resultHeader}>
                <h3 style={styles.resultTitle}>
                  <Brain size={20} style={styles.iconBlue} />
                  5-Year Cognitive Impairment Risk
                </h3>
              </div>
              
              {risks.cognitive !== null ? (
                <div>
                  <div style={{...styles.resultValue, ...styles.cognitiveValue}}>
                    {risks.cognitive.toFixed(1)}%
                  </div>
                  <div style={{...styles.riskBadge, ...getRiskLevel(risks.cognitive).style}}>
                    {getRiskLevel(risks.cognitive).level} Risk
                  </div>
                </div>
              ) : (
                <p style={styles.placeholder}>Complete required fields to calculate</p>
              )}
            </div>

            {/* ASCVD Risk Result */}
            <div style={{...styles.resultCard, ...styles.ascvdCard}}>
              <div style={styles.resultHeader}>
                <h3 style={styles.resultTitle}>
                  <Heart size={20} style={styles.iconRed} />
                  5-Year ASCVD Risk
                </h3>
              </div>
              
              {risks.ascvd !== null ? (
                <div>
                  <div style={{...styles.resultValue, ...styles.ascvdValue}}>
                    {risks.ascvd.toFixed(1)}%
                  </div>
                  <div style={{...styles.riskBadge, ...getRiskLevel(risks.ascvd).style}}>
                    {getRiskLevel(risks.ascvd).level} Risk
                  </div>
                </div>
              ) : (
                <p style={styles.placeholder}>Complete all cardiovascular fields to calculate</p>
              )}
            </div>

            {/* Information Box */}
            <div style={styles.infoBox}>
              <div style={styles.infoHeader}>
                <AlertCircle size={20} style={styles.infoIcon} />
                <div>
                  <p style={{fontWeight: '600', marginBottom: '4px', fontSize: '14px'}}>Important Notes:</p>
                  <ul style={{listStyle: 'disc', paddingLeft: '20px', fontSize: '12px', color: '#856404'}}>
                    <li>This calculator is for adults aged 65+ without prior CVD events</li>
                    <li>Results should be interpreted by qualified healthcare professionals</li>
                    <li>Based on multi-cohort US data; may need adjustment for Australian populations</li>
                    <li>All laboratory values use Australian units and are converted automatically</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Unit Conversion Info */}
            <div style={styles.conversionBox}>
              <h4 style={styles.conversionTitle}>
                <Calculator size={16} />
                Unit Conversions Applied
              </h4>
              <div style={styles.conversionText}>
                <p>• HDL & Triglycerides: mmol/L → mg/dL (×38.67)</p>
                <p>• Creatinine: μmol/L → mg/dL (÷88.4)</p>
              </div>
            </div>

            {/* Reference / Footnote */}
            <div style={{marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e0e0e0'}}>
              <h4 style={{...styles.conversionTitle, fontSize: '14px', marginBottom: '8px'}}>Reference:</h4>
              <p style={{...styles.conversionText, fontSize: '12px'}}>
                Nanna M, et al. Predicting 5-Year Risk of Cognitive Impairment and ASCVD in Older Adults: A Multi-Cohort Study. <em>J Am Heart Assoc</em>. 2025;14:11. DOI: <a href="https://www.ahajournals.org/doi/10.1161/JAHA.124.038949" target="_blank" rel="noopener noreferrer">10.1161/JAHA.124.038949</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskCalculator;